import {useState} from 'react'
import isEqual from 'lodash/isEqual.js'
import isNil from 'lodash/isNil.js'

import OmniBar, {parseInputValue} from '../../common/components/List/OmniBar.js'
import {
  TAG_FILTER_OR,
  TAG_FILTER_AND,
  TAG_FILTER_ONLY,
  TAG_FILTER_NOT,
} from '../../common/constants/Tags.js'

import {
  getWarehouseName,
  warehousesSortedByNameSelector,
} from '../../data/warehouses.js'
import {stocktakeTagsSortedByNameSelector} from '../../data/stocktakeTags.js'
import {
  setSearchText,
  updateStocktakeTags,
  updateStocktakeUntagged,
  setStocktakeTagFilterBy,
  stocktakeListQuerySelector,
  navigateStocktakeList,
  setStocktakeWarehouseIDs,
  removeStocktakeWarehouseID,
  setStocktakeUntagged,
  removeStocktakeTagFilter,
  navigateToStocktakeListPage,
  setStocktakeTroolean,
  removeStocktakeExcludeTag,
  updateStocktakeExcludeTags,
} from './stocktakeListFunctions.js'
import {getState, useSelector} from '../../store.js'
import {
  STOCKTAKE_TROOLEAN_FILTER_OPTION_TOKENS,
  STOCKTAKE_TROOLEAN_FILTER_OPTIONS,
} from '../../common/constants/Stocktakes.js'
import {filterOptions} from '../../common/tokenizeOptions.js'

export const SEARCH_TYPE = 'search'
export const WAREHOUSE_TYPE = 'warehouse'
export const FILTERS_TYPE = 'filters'
export const TAG_TYPE = 'tag'
export const TAG_AND_TYPE = 'tag_and'
export const TAG_ONLY_TYPE = 'tag_only'
export const TAG_NOT_TYPE = 'tag_not'
const TAG_TYPES = {
  [TAG_FILTER_OR]: TAG_TYPE,
  [TAG_FILTER_AND]: TAG_AND_TYPE,
  [TAG_FILTER_ONLY]: TAG_ONLY_TYPE,
  [TAG_FILTER_NOT]: TAG_NOT_TYPE,
}
export const UNTAGGED_TYPE = 'untagged'
export const EXCLUDE_TAGS_TYPE = 'exclude_tags'

export function stocktakeListOmniBarFilterListSelector(state) {
  const cache = stocktakeListOmniBarFilterListSelector.cache
  const warehouses = warehousesSortedByNameSelector(state)
  const stocktakeTags = stocktakeTagsSortedByNameSelector(state)
  const query = stocktakeListQuerySelector(state)

  if (
    cache &&
    isEqual(warehouses, cache.warehouses) &&
    isEqual(stocktakeTags, cache.stocktakeTags) &&
    query === cache.query
  ) {
    return cache.list
  }

  stocktakeListOmniBarFilterListSelector.cache = {
    warehouses,
    stocktakeTags,
    query,
  }

  const list = stocktakeFilterList({
    warehouses,
    stocktakeTags,
    query,
  })

  stocktakeListOmniBarFilterListSelector.cache.list = list

  return list
}

export function stocktakeFilterList({
  warehouses,
  stocktakeTags,
  query: {
    started,
    completed,
    cancelled,
    warehouse_id = [],
    tag = [],
    tag_filter_by,
    exclude_tags = [],
    untagged,
    search,
  },
}) {
  const list = []

  const filters = {started, completed, cancelled}
  STOCKTAKE_TROOLEAN_FILTER_OPTIONS.forEach((filter) => {
    if (isNil(filters[filter.value])) {
      return
    }

    list.push({
      type: FILTERS_TYPE,
      label: filters[filter.value] ? filter.display : filter.reverseDisplay,
      value: filter.value,
    })
  })

  warehouses.forEach((warehouse) => {
    if (!warehouse_id.includes(warehouse.id)) {
      return
    }

    list.push({
      type: WAREHOUSE_TYPE,
      label: getWarehouseName(warehouse),
      value: `${WAREHOUSE_TYPE}:${warehouse.id}`,
      id: warehouse.id,
    })
  })

  const tagType = TAG_TYPES[tag_filter_by || TAG_FILTER_OR]
  for (const t of stocktakeTags) {
    if (tag.includes(t.name)) {
      list.push({
        type: tagType,
        label: t.name,
        value: `${tagType}:${t.id}`,
        color: t.color,
        id: t.id,
      })
    }
  }

  for (const t of stocktakeTags) {
    if (exclude_tags.includes(t.name)) {
      list.push({
        type: EXCLUDE_TAGS_TYPE,
        label: t.name,
        value: `${EXCLUDE_TAGS_TYPE}:${t.id}`,
        color: t.color,
        name: t.name,
      })
    }
  }

  if (untagged) {
    list.push({
      type: UNTAGGED_TYPE,
      label: 'Untagged',
      value: 'untagged',
    })
  }

  if (search) {
    list.push({
      type: SEARCH_TYPE,
      label: search,
      value: search,
    })
  }

  return list
}

export const PROCESS_VALUE_FUNCTIONS = {
  processStatusFilterValue(
    inputValue,
    filterType,
    filterOperator,
    filterValue,
  ) {
    const outcome = {stop: false}

    if (
      !(filterType.match(/^status$/i) && ['', '-'].includes(filterOperator))
    ) {
      return outcome
    }

    outcome.stop = true

    let reverse = filterOperator === '-'

    const options = filterOptions(
      filterValue,
      STOCKTAKE_TROOLEAN_FILTER_OPTION_TOKENS,
    )

    if (options.length) {
      outcome.hash = setStocktakeTroolean({
        [options[0].value]: reverse ? false : true,
      })
    }

    return outcome
  },

  processWarehouseFilterValue(
    inputValue,
    filterType,
    filterOperator,
    filterValue,
  ) {
    const outcome = {stop: false}

    if (
      !(
        filterType.match(/^warehouse[s]?$/i) &&
        ['', '='].includes(filterOperator)
      )
    ) {
      return outcome
    }

    outcome.stop = true

    const warehouses = warehousesSortedByNameSelector(getState())

    const matchedWarehouses = warehouses.filter((w) => {
      if (filterOperator === '=') {
        if (w.id === Number(filterValue)) {
          return true
        }
        if (getWarehouseName(w) === filterValue) {
          return true
        }
      } else {
        if (
          `${w.address.name} ${w.address.company} ${w.address.city} ${w.address.state}`.match(
            RegExp(filterValue, 'i'),
          )
        ) {
          return true
        }
      }

      return false
    })

    if (matchedWarehouses.length) {
      outcome.hash = setStocktakeWarehouseIDs([
        ...stocktakeListQuerySelector(getState()).warehouse_id,
        ...matchedWarehouses.map(({id}) => id),
      ])
    }

    return outcome
  },

  processTagFilterValue(inputValue, filterType, filterOperator, filterValue) {
    const outcome = {stop: false}

    if (
      !(
        filterType.match(/^(tag)[s]?$/i) &&
        ['', '=', '-', '!', '!='].includes(filterOperator)
      )
    ) {
      return outcome
    }

    outcome.stop = true

    const productTags = stocktakeTagsSortedByNameSelector(getState())
    const query = stocktakeListQuerySelector(getState())

    const matchedTags = productTags.filter((t) => {
      if (['=', '!='].includes(filterOperator)) {
        if (t.id === Number(filterValue)) {
          return true
        }
        if (t.name === filterValue) {
          return true
        }
      } else {
        if (`${t.name}`.match(RegExp(filterValue, 'i'))) {
          return true
        }
      }

      return false
    })

    const negate = ['!', '-', '!='].includes(filterOperator)

    if (matchedTags.length) {
      let updates = updateStocktakeUntagged(false)

      if (negate) {
        updates = {
          ...updates,
          ...updateStocktakeExcludeTags([
            ...query.exclude_tags,
            ...matchedTags.map(({name}) => name),
          ]),
        }
      } else {
        updates = {
          ...updates,
          ...updateStocktakeTags([
            ...query.tag,
            ...matchedTags.map(({name}) => name),
          ]),
        }
      }
      outcome.hash = navigateStocktakeList(updates)
    }

    return outcome
  },

  processUntaggedValue(inputValue, filterType, filterOperator, filterValue) {
    const outcome = {stop: false}

    if (!filterValue.match(/^untag(ged)?$/i)) {
      return outcome
    }

    outcome.stop = true

    outcome.hash = navigateStocktakeList({
      ...updateStocktakeUntagged(true),
      ...updateStocktakeTags([]),
    })

    return outcome
  },

  processTagFilterByValue(inputValue, filterType, filterOperator, filterValue) {
    const outcome = {stop: false}

    if (
      !(
        filterType.match(/^(tag.filter(ing)?.logic|tfl)$/i) &&
        ['', '='].includes(filterOperator)
      )
    ) {
      return outcome
    }

    outcome.stop = true

    const filterBy = filterValue.match(/^(and|all)$/i)
      ? TAG_FILTER_AND
      : filterValue.match(/^only$/i)
        ? TAG_FILTER_ONLY
        : filterValue.match(/^not$/i)
          ? TAG_FILTER_NOT
          : TAG_FILTER_OR

    outcome.hash = setStocktakeTagFilterBy(filterBy)

    return outcome
  },

  // Put new functions before this function
  processSearchTextFilterValue(inputValue) {
    return {stop: true, hash: setSearchText(inputValue)}
  },
  // Do not put functions after this function
}

export function processInputValue(inputValue, setInputValue) {
  if (!inputValue) {
    setInputValue('')

    return
  }

  const [, filterType, filterOperator, filterValue] =
    parseInputValue(inputValue)

  let hash
  for (const processFilterValue of Object.values(PROCESS_VALUE_FUNCTIONS)) {
    const outcome = processFilterValue(
      inputValue,
      filterType,
      filterOperator,
      filterValue,
    )

    if (outcome.stop) {
      hash = outcome.hash
      break
    }
  }

  if (hash) {
    setInputValue('')

    return hash
  }

  return
}

export function removeValue(value) {
  if (value.type === FILTERS_TYPE) {
    return setStocktakeTroolean({[value.value]: null})
  } else if (value.type === WAREHOUSE_TYPE) {
    return removeStocktakeWarehouseID(value.id)
  } else if (
    [TAG_TYPE, TAG_AND_TYPE, TAG_ONLY_TYPE, TAG_NOT_TYPE].includes(value.type)
  ) {
    return removeStocktakeTagFilter(value.label)
  } else if (value.type === UNTAGGED_TYPE) {
    return setStocktakeUntagged(false)
  } else if (value.type === EXCLUDE_TAGS_TYPE) {
    return removeStocktakeExcludeTag(value.label)
  } else if (value.type === SEARCH_TYPE) {
    return setSearchText('')
  }
}

export function clearAll() {
  return navigateToStocktakeListPage()
}

export function editValue(data, setInputValue) {
  if (data.type === SEARCH_TYPE) {
    setInputValue(data.value)
  }
}

export default function StocktakeListOmniBar() {
  const [inputValue, setInputValue] = useState('')
  const list = useSelector(stocktakeListOmniBarFilterListSelector)

  return (
    <OmniBar
      placeholder="Enter a Stocktake Reference ID..."
      inputValue={inputValue}
      value={list}
      onRemoveValue={(removedValue) => removeValue(removedValue)}
      onClear={() => clearAll()}
      onInputValueChange={(inputValue) => setInputValue(inputValue)}
      onProcessInputValue={(inputValue) =>
        processInputValue(inputValue, setInputValue)
      }
      onEditValue={(data) => editValue(data, setInputValue)}
    />
  )
}
